import axios from 'axios';
import { knowledgeBaseClient } from '../knowledge-base-client';

/**
 * Knowledge Base service layer.
 *
 * Migrated from Wexa Studio (src/api/files). The Knowledge Base is served by the
 * external Python data-service — the SAME backend Wexa Studio talks to — reused
 * as-is. File binaries are stored in Azure blob storage and embeddings are
 * produced by the data-service ingest → Qdrant pipeline. The context-service (Go)
 * backend is intentionally NOT involved.
 *
 * Wire format note: the data-service speaks the Wexa contract (snake_case fields,
 * `_id`, numeric `uploaded_on`, root `files`/`file`/`tagsbyprojectId` routes). This
 * module is the ONLY place that knows about that wire format — it maps the
 * data-service `FileInDB` shape onto the camelCase `KnowledgeFile` the UI/shared
 * components consume, so the components never changed during the migration.
 */

export type IngestionStatus =
  | 'scheduled'
  | 'running'
  | 'completed'
  | 'ready'
  | 'failed';

export type SourceType = 'STORAGE' | 'GENERAL';

/** UI-facing model (camelCase). Stable across the migration. */
export interface KnowledgeFile {
  _id: string;
  id?: string; // alias populated from _id for backwards compat
  projectId: string;
  projectID?: string;
  name: string;
  tags: string[];
  sourceType: SourceType;
  source_type?: SourceType;
  sizeInKb: number;
  size_in_kb?: number;
  mimeType: string;
  mime_type?: string;
  url?: string;
  location?: string;
  container_name?: string;
  connector_id?: string;
  status: IngestionStatus;
  /** Receipt from the last ingest. Absent for files ingested before it shipped. */
  ingestionStats?: IngestionReceipt;
  uploadedOn?: string;
  uploaded_on?: number; // Unix timestamp from data service
  createdAt?: string;
  updatedAt?: string;
}

/**
 * What one ingest wrote for a document.
 *
 * Two blocks that must not be conflated. `submitted` is the receipt the ingest
 * itself recorded: exact, and present even when the graph is unreachable.
 * `confirmed` is counted live from the graph when the page asks, and is the only
 * honest answer to "were the nodes created", because graph landing is an
 * asynchronous harness workflow that returns before the rows exist. A document
 * ingested before this shipped has no receipt but still has confirmed counts.
 */
export interface IngestionNodeCounts {
  documents: number;
  chunks: number;
  entities: number;
  mentions: number;
  relations: number;
  total: number;
}

export interface IngestionEdgeCounts {
  IN_DOCUMENT: number;
  MENTIONED_IN: number;
  OF_ENTITY: number;
  DERIVED_FROM: number;
  total: number;
}

/** 'submitted' means harness accepted the rows, not that they are queryable yet. */
export type GraphLandingStatus =
  | 'submitted'
  | 'skipped'
  | 'failed'
  | 'unknown';

export interface IngestionReceipt {
  document_id?: string;
  project_id?: string;
  connector_id?: string;
  started_at?: string;
  finished_at?: string;
  duration_seconds?: number;
  files?: string[];
  images_count?: number;
  tables_count?: number;
  vector?: {
    points_upserted: number;
    collection?: string;
    seconds?: number | null;
    collection_points_total?: number | null;
  };
  graph?: {
    status: GraphLandingStatus;
    detail?: string;
    nodes: IngestionNodeCounts;
    edges: IngestionEdgeCounts;
    extracted?: boolean;
    deferred_chunks?: number;
    batches_sent?: number;
    seconds?: number | null;
  };
}

export interface ConfirmedGraphCounts {
  available: boolean;
  found?: boolean;
  reason?: string;
  nodes?: IngestionNodeCounts;
  edges?: IngestionEdgeCounts;
  ingested_at?: string | null;
  checked_at?: string;
}

/** Server-computed phase. The browser never decides what "finished" means. */
export type IngestionPhase =
  | 'queued'
  | 'extracting'
  | 'vectorised'
  | 'landing'
  | 'complete'
  | 'skipped'
  | 'failed';

export interface IngestionProgress {
  phase: IngestionPhase;
  percent: number;
  label: string;
  detail: string;
  done: boolean;
}

export interface DocumentIngestionProgress {
  file_id: string;
  connector_id?: string;
  connector_status?: string;
  progress: IngestionProgress;
  submitted?: IngestionReceipt | null;
  confirmed?: ConfirmedGraphCounts;
}

export interface IngestionProgressResponse {
  project_id: string;
  documents: DocumentIngestionProgress[];
}

export interface FileIngestion {
  file_id: string;
  project_id: string;
  connector_id?: string;
  status?: IngestionStatus;
  submitted?: IngestionReceipt | null;
  confirmed?: ConfirmedGraphCounts;
}

export interface ListFilesParams {
  projectId: string;
  search?: string;
  skip?: number;
  limit?: number;
  sortOrder?: 'asc' | 'desc';
}

export interface ListFilesResponse {
  files: KnowledgeFile[];
  total_count: number;
}

export interface TagsResponse {
  tags: string[];
  total_count: number;
}

export interface UpdateFileInput {
  name?: string;
  tags?: string[];
}

export interface UploadFileInput {
  /** The files to upload. Each real name (with extension) determines its type. */
  files: File[];
  /** Tags to associate with the files. The data-service requires at least one. */
  tags?: string[];
}

// ─── retrieval (query / test console) ─────────────────────────────────────────

/** A single related image attached to a retrieval result set. */
export interface RetrievedImage {
  imageId: string;
  imageUrl?: string;
  fileId: string;
  caption?: string;
  imageCaption?: string;
  imageDescription?: string;
}

/** A single related table attached to a retrieval result set. */
export interface RetrievedTable {
  tableId: string;
  tableData: string[][];
  fileId: string;
  caption?: string;
}

export interface RetrievedRelatedContent {
  images: RetrievedImage[];
  tables: RetrievedTable[];
}

/** A single retrieved knowledge-base chunk (text / image / table). */
export interface RetrievedChunk {
  id: string;
  content: string;
  elementType: 'text' | 'image' | 'table' | string;
  /** Raw metadata payload — surfaced verbatim in the console for inspection. */
  metadata: Record<string, unknown>;
  /** Source file the chunk was extracted from (best-effort from metadata). */
  fileId?: string;
  /** Human-readable source name when present in metadata. */
  fileName?: string;
  imageId?: string;
  imageUrl?: string;
  tableId?: string;
  tableData?: string[][];
}

export interface RetrieveKnowledgeParams {
  projectId: string;
  organizationId: string;
  /** Natural-language query (the "goal") used for semantic re-ranking. */
  goal?: string;
  /** Tags to scope retrieval. The data-service requires the field but accepts []. */
  tags?: string[];
  /** Max chunks to return (default 10). */
  limit?: number;
  /** Include base64 image payloads (heavier responses). Default false. */
  includeImageData?: boolean;
}

export interface RetrieveKnowledgeResult {
  chunks: RetrievedChunk[];
  relatedContent: RetrievedRelatedContent;
}

// ─── data-service wire types (Wexa contract) ──────────────────────────────────

interface FileInDB {
  _id: string;
  projectID: string;
  name: string;
  location?: string;
  tags?: string[];
  source_type?: string;
  size_in_kb?: number;
  container_name?: string;
  url?: string;
  connector_id?: string;
  uploaded_on?: number; // epoch (seconds or ms)
  status?: IngestionStatus;
  mime_type?: string;
  ingestion_stats?: IngestionReceipt;
}

interface ListFilesWire {
  files?: FileInDB[];
  total_count?: number;
}

interface TagsWire {
  tags?: string[];
  total_count?: number;
}

interface UploadURL {
  url: string;
  filename: string;
  mimeType: string;
  _id: string;
}

interface UploadUrlsWire {
  status: string;
  upload_urls: UploadURL[];
}

// retrieval wire types (snake_case, mirrors RetrieveKnowledgeBaseResponse)
interface ImageContentWire {
  image_id: string;
  image_url?: string | null;
  file_id: string;
  caption?: string | null;
  image_caption?: string | null;
  image_description?: string | null;
}

interface TableContentWire {
  table_id: string;
  table_data: string[][];
  file_id: string;
  caption?: string | null;
}

interface RelatedContentWire {
  images?: ImageContentWire[];
  tables?: TableContentWire[];
}

interface RetrieveDataWire {
  id: string;
  content: string;
  element_type?: string;
  metadata?: Record<string, unknown>;
  image_id?: string | null;
  image_data?: string | null;
  image_url?: string | null;
  table_id?: string | null;
  table_data?: string[][] | null;
}

interface RetrieveResponseWire {
  data?: RetrieveDataWire[];
  related_content?: RelatedContentWire | null;
}

// ─── mappers ──────────────────────────────────────────────────────────────────

function epochToISO(value?: number): string {
  if (!value) return '';
  // Heuristic: treat 10-digit values as seconds, 13-digit as milliseconds.
  const ms = value < 1e12 ? value * 1000 : value;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

function mapFile(f: FileInDB): KnowledgeFile {
  const uploaded = epochToISO(f.uploaded_on);
  return {
    _id: f._id,
    id: f._id,
    projectId: f.projectID,
    name: f.name,
    tags: f.tags ?? [],
    sourceType: (f.source_type as SourceType) ?? 'STORAGE',
    sizeInKb: f.size_in_kb ?? 0,
    mimeType: f.mime_type ?? '',
    url: f.url,
    status: f.status ?? 'scheduled',
    ingestionStats: f.ingestion_stats,
    uploadedOn: uploaded,
    createdAt: uploaded,
    updatedAt: uploaded,
  };
}

/** Pull a human-readable source name out of a chunk's metadata if present. */
function sourceNameFromMetadata(meta: Record<string, unknown>): string | undefined {
  const candidate =
    meta.filename ?? meta.file_name ?? meta.name ?? meta.source ?? meta.title;
  return typeof candidate === 'string' && candidate.trim() ? candidate : undefined;
}

function mapChunk(d: RetrieveDataWire): RetrievedChunk {
  const metadata = (d.metadata ?? {}) as Record<string, unknown>;
  const fileId =
    typeof metadata.file_id === 'string' && metadata.file_id
      ? metadata.file_id
      : undefined;
  return {
    id: d.id,
    content: d.content ?? '',
    elementType: d.element_type ?? 'text',
    metadata,
    fileId,
    fileName: sourceNameFromMetadata(metadata),
    imageId: d.image_id ?? undefined,
    imageUrl: d.image_url ?? undefined,
    tableId: d.table_id ?? undefined,
    tableData: d.table_data ?? undefined,
  };
}

function mapRelatedContent(rc?: RelatedContentWire | null): RetrievedRelatedContent {
  return {
    images: (rc?.images ?? []).map((i) => ({
      imageId: i.image_id,
      imageUrl: i.image_url ?? undefined,
      fileId: i.file_id,
      caption: i.caption ?? undefined,
      imageCaption: i.image_caption ?? undefined,
      imageDescription: i.image_description ?? undefined,
    })),
    tables: (rc?.tables ?? []).map((t) => ({
      tableId: t.table_id,
      tableData: t.table_data ?? [],
      fileId: t.file_id,
      caption: t.caption ?? undefined,
    })),
  };
}

// ─── service ──────────────────────────────────────────────────────────────────

const pid = (projectId: string) => encodeURIComponent(projectId);

export const knowledgeBaseApi = {
  /** PUT /files/{projectId} — list files (Wexa uses PUT with a body filter). */
  async listFiles({
    projectId,
    search,
    skip = 1,
    limit = 16,
    sortOrder = 'desc',
  }: ListFilesParams): Promise<ListFilesResponse> {
    const body = {
      limit,
      // Wexa Studio's UI `skip` is a 1-based page; the data-service expects an
      // offset, so convert here.
      skip: (Math.max(skip, 1) - 1) * limit,
      ...(search ? { search } : {}),
      ...(sortOrder ? { sort_order: sortOrder } : {}),
    };
    const data = await knowledgeBaseClient.put<ListFilesWire>(
      `/files/${pid(projectId)}?projectID=${pid(projectId)}`,
      body,
    );
    return {
      files: (data?.files ?? []).map(mapFile),
      total_count: data?.total_count ?? 0,
    };
  },

  /** GET /file/{projectId}/{fileId} — single file. */
  async getFile(projectId: string, fileId: string): Promise<KnowledgeFile> {
    const data = await knowledgeBaseClient.get<FileInDB>(
      `/file/${pid(projectId)}/${encodeURIComponent(fileId)}?projectID=${pid(projectId)}`,
    );
    return mapFile(data);
  },

  /**
   * POST /files/{projectId}/ingestion-progress — phase and counts for a page of
   * documents. One call rather than one per card, and one graph query behind it.
   */
  async getIngestionProgress(
    projectId: string,
    fileIds: string[],
  ): Promise<IngestionProgressResponse> {
    return knowledgeBaseClient.post<IngestionProgressResponse>(
      `/files/${pid(projectId)}/ingestion-progress?projectID=${pid(projectId)}`,
      { file_ids: fileIds },
    );
  },

  /** GET /file/{projectId}/{fileId}/ingestion — what the ingest wrote. */
  async getFileIngestion(
    projectId: string,
    fileId: string,
  ): Promise<FileIngestion> {
    return knowledgeBaseClient.get<FileIngestion>(
      `/file/${pid(projectId)}/${encodeURIComponent(fileId)}/ingestion?projectID=${pid(projectId)}`,
    );
  },

  /** PUT /files/{projectId}/{fileId} — update name/tags. */
  async updateFile(
    projectId: string,
    fileId: string,
    input: UpdateFileInput,
  ): Promise<KnowledgeFile> {
    const data = await knowledgeBaseClient.put<FileInDB>(
      `/files/${pid(projectId)}/${encodeURIComponent(fileId)}?projectID=${pid(projectId)}`,
      input,
    );
    return mapFile(data);
  },

  /** DELETE /file/{projectId} — body is an array of file ids. */
  deleteFile: (projectId: string, fileId: string) =>
    knowledgeBaseClient.delete<{ status?: string }>(
      `/file/${pid(projectId)}?projectID=${pid(projectId)}`,
      [fileId],
    ),

  /** GET /tagsbyprojectId/{projectId} — tag autocomplete. */
  async listTags(projectId: string, searchKey?: string): Promise<TagsResponse> {
    const data = await knowledgeBaseClient.get<TagsWire>(
      `/tagsbyprojectId/${pid(projectId)}`,
      { params: searchKey ? { search_key: searchKey } : {} },
    );
    return { tags: data?.tags ?? [], total_count: data?.total_count ?? 0 };
  },

  /**
   * POST /knowledge_base/retrieve — semantic + tag retrieval over the project's
   * vector store (Qdrant). Powers the Knowledge Base query/test console.
   *
   * `tags` filter the candidate set and `goal` (the natural-language query) drives
   * the semantic/lexical re-ranking. The data-service requires the `tags` body
   * field but accepts an empty array (retrieve across all tags).
   */
  async retrieveKnowledge({
    projectId,
    organizationId,
    goal,
    tags = [],
    limit = 10,
    includeImageData = false,
  }: RetrieveKnowledgeParams): Promise<RetrieveKnowledgeResult> {
    const params = new URLSearchParams();
    params.set('projectID', projectId);
    params.set('organization_id', organizationId);
    params.set('limit', String(limit));
    params.set('include_image_data', String(includeImageData));
    if (goal && goal.trim()) params.set('goal', goal.trim());

    const data = await knowledgeBaseClient.post<RetrieveResponseWire>(
      `/knowledge_base/retrieve?${params.toString()}`,
      tags,
    );
    return {
      chunks: (data?.data ?? []).map(mapChunk),
      relatedContent: mapRelatedContent(data?.related_content),
    };
  },

  /** POST /files/{projectId}/ingest — trigger embedding (→ Qdrant). */
  ingestFiles: (projectId: string, fileIds: string[]) =>
    knowledgeBaseClient.post<UploadUrlsWire | unknown>(
      `/files/${pid(projectId)}/ingest?projectID=${pid(projectId)}`,
      fileIds,
    ),

  /**
   * Two-step upload exactly as in Wexa Studio, batched over any number of files
   * in a single round trip:
   *   1. POST /files/upload          → request one pre-signed Azure blob URL per file.
   *   2. PUT <azure-url>             → upload each binary directly to blob storage.
   * Then the caller triggers ingest. Returns the created file ids (request order).
   */
  async uploadFile(
    projectId: string,
    { files, tags = [] }: UploadFileInput,
  ): Promise<string[]> {
    // Send the REAL filenames (with extension). The data-service derives each
    // file's type from the extension via mimetypes.guess_type — a stripped name
    // yields "File type None is not supported". This matches Wexa Studio exactly.
    const filenames = files.map((file) => file.name);

    const res = await knowledgeBaseClient.post<UploadUrlsWire>(
      `/files/upload?projectID=${pid(projectId)}&container_name=${pid(projectId)}`,
      {
        // The data-service FileUpload contract requires exactly these three fields.
        // projectID is taken from the query string and org_id from the auth token,
        // so they are intentionally NOT sent in the body.
        source_type: 'STORAGE' as SourceType,
        filenames,
        tags,
      },
    );

    if (!res?.upload_urls?.length) {
      throw new Error('Could not obtain an upload URL');
    }

    // The data-service issues upload_urls in the same order it received
    // filenames (see add.py's `for filename in file_upload.filenames` loop), so
    // index-based pairing is safe even when two selected files share a name.
    const uploadUrls = res.upload_urls;

    try {
      // Upload every binary in parallel via a SAME-ORIGIN proxy instead of
      // PUTting straight to Azure Blob Storage. A direct browser PUT to
      // *.blob.core.windows.net is cross-origin and is blocked unless the
      // storage account's CORS allows this origin (it doesn't). The proxy
      // forwards to the signed URL server-side, where CORS does not apply; the
      // SAS stays valid because the URL is unchanged.
      await Promise.all(
        uploadUrls.map((match, i) => {
          const file = files[i];
          const blobUploadUrl = `/api/blob-proxy?target=${encodeURIComponent(match.url)}`;
          return axios.put(blobUploadUrl, file, {
            headers: {
              'x-ms-blob-type': 'BlockBlob',
              'Content-Type': file.type || 'application/octet-stream',
            },
          });
        }),
      );
    } catch (err) {
      // The data-service creates each file's metadata record at /files/upload,
      // BEFORE the blob exists. If any blob PUT fails, treat the whole batch as
      // failed and roll back every record (best-effort) so a partial upload
      // can't poison the Knowledge Base or leave the list endpoint 500ing on a
      // missing blob.
      await Promise.all(
        uploadUrls.map((u) =>
          knowledgeBaseClient
            .delete(`/file/${pid(projectId)}?projectID=${pid(projectId)}`, [u._id])
            .catch(() => undefined),
        ),
      );
      throw err;
    }

    return uploadUrls.map((u) => u._id);
  },
};
